import PropTypes from 'prop-types';
import { Card, CardHeader, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { mrrBreakdown } from '../../../helpers/mrr';

// Mes en formato 'YYYY-MM' → Date al 1 del mes.
const parseMonthKey = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1);
};

const monthLabel = (date) => date.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }).replace('.', '');

const fEur2 = (v) =>
  `${(Number(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Construye una serie continua (sin huecos) desde el mes actual hasta el último mes con renovaciones.
// MRR: mes actual = MRR base + tramo "Nuevo MRR" (ver helpers/mrr.js; las bajas que terminan este
// mes no se cuentan), siguiente = monthlyBillingNextMonth, posteriores = se asume constante = monthlyBillingNextMonth.
function buildForecast(stats) {
  if (!stats) return [];

  const renewalsMap = {};
  const keys = [];
  (stats.renewalsByMonth || []).forEach((m) => {
    if (!m?.month) return;
    renewalsMap[m.month] = Number(m.total) || 0;
    keys.push(m.month);
  });

  // Cancelaciones de mensuales: ahora `stats.canceledMonthly` es la lista de mensuales que se dan
  // de baja al cierre del periodo en curso. Ese conteo explica el salto de MRR a
  // `monthlyBillingNextMonth`. No hay desglose por mes para meses futuros, así que sólo lo
  // pintamos en el mes actual.
  const monthlyCancelThisMonth = Array.isArray(stats.canceledMonthly)
    ? stats.canceledMonthly.length
    : Number(stats.monthlyCancelations) || 0;
  const initialActiveMonthly = Number(stats.activeMonthlySubscriptions) || 0;

  // Nuevas suscripciones anuales del mes: el backend da un único `totalEUR` (sin desglose
  // por mes), así que solo se reflejan en el mes en curso — igual que las cancelaciones de
  // mensuales. Su primer cobro entra en caja ya, pero no es renovación (la renovación cae
  // dentro de un año), de ahí la barra propia.
  const newAnnualThisMonth = Number(stats.newAnnualSubscriptionsThisMonth?.totalEUR) || 0;
  const currentMrr = mrrBreakdown(stats);

  // Las claves 'YYYY-MM' ordenan correctamente como strings.
  const lastKey = keys.length > 0 ? keys.sort()[keys.length - 1] : null;
  const now = new Date();
  const cursor = new Date(now.getFullYear(), now.getMonth(), 1);
  // Si no hay renovaciones, mostramos al menos mes actual + siguiente
  const fallbackEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const lastDate = lastKey ? parseMonthKey(lastKey) : fallbackEnd;

  const out = [];
  let offset = 0;
  while (cursor <= lastDate || offset === 0) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    const mrr = offset === 0 ? currentMrr.base : Number(stats.monthlyBillingNextMonth) || 0;
    // Mes actual: total activas + las que cancelarán al cierre. Mes siguiente y posteriores:
    // arrancan con las que efectivamente quedan tras la baja del mes en curso.
    const activeMonthly =
      offset === 0 ? initialActiveMonthly : Math.max(initialActiveMonthly - monthlyCancelThisMonth, 0);
    out.push({
      key,
      label: monthLabel(cursor),
      mrr,
      newMrr: offset === 0 ? currentMrr.newMrr : 0,
      renewals: renewalsMap[key] || 0,
      newAnnual: offset === 0 ? newAnnualThisMonth : 0,
      activeMonthly,
      canceledMonthly: offset === 0 ? monthlyCancelThisMonth : null,
    });
    cursor.setMonth(cursor.getMonth() + 1);
    offset += 1;
    if (out.length > 36) break; // safety guard
  }
  return out;
}

export default function ForecastChart({ stats }) {
  const theme = useTheme();
  const forecast = buildForecast(stats);

  if (forecast.length === 0) return null;

  const series = [
    { name: 'MRR', data: forecast.map((m) => Number(m.mrr.toFixed(2))) },
    { name: 'Nuevo MRR', data: forecast.map((m) => Number(m.newMrr.toFixed(2))) },
    { name: 'Renovaciones', data: forecast.map((m) => Number(m.renewals.toFixed(2))) },
    { name: 'Nuevas anuales', data: forecast.map((m) => Number(m.newAnnual.toFixed(2))) },
  ];

  const options = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: theme.typography.fontFamily },
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
    colors: [
      theme.palette.primary.main,
      theme.palette.primary.light,
      theme.palette.warning.main,
      theme.palette.success.main,
    ],
    dataLabels: { enabled: false },
    xaxis: {
      categories: forecast.map((m) => m.label),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { formatter: (v) => `${Math.round(v).toLocaleString('es-ES')} €` },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ series: s, dataPointIndex, w }) => {
        const m = forecast[dataPointIndex];
        if (!m) return '';
        const mrr = Number(s[0]?.[dataPointIndex]) || 0;
        const newMrr = Number(s[1]?.[dataPointIndex]) || 0;
        const renewals = Number(s[2]?.[dataPointIndex]) || 0;
        const newAnnual = Number(s[3]?.[dataPointIndex]) || 0;
        const total = mrr + newMrr + renewals + newAnnual;
        const colorMrr = w?.globals?.colors?.[0] || theme.palette.primary.main;
        const colorNewMrr = w?.globals?.colors?.[1] || theme.palette.primary.light;
        const colorRen = w?.globals?.colors?.[2] || theme.palette.warning.main;
        const colorNew = w?.globals?.colors?.[3] || theme.palette.success.main;
        // Solo el mes en curso tiene tramo de nuevo MRR; ahí la línea MRR es el base.
        const newMrrRow =
          newMrr > 0
            ? `<div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colorNewMrr};margin-right:6px;"></span>Nuevo MRR: ${fEur2(
                newMrr
              )}</div>`
            : '';
        const cancelText =
          m.canceledMonthly > 0
            ? `, <strong style="color:${theme.palette.warning.main}">${m.canceledMonthly} cancelarán</strong>`
            : '';
        // Solo el mes en curso tiene nuevas anuales — en el resto se omite la línea.
        const newAnnualRow =
          newAnnual > 0
            ? `<div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colorNew};margin-right:6px;"></span>Nuevas anuales: ${fEur2(
                newAnnual
              )}</div>`
            : '';
        return `
          <div style="padding:8px 12px;font-size:12px;line-height:1.5;">
            <div style="font-weight:600;margin-bottom:4px;">${m.label}</div>
              <div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colorMrr};margin-right:6px;"></span>${
          newMrr > 0 ? 'MRR base' : 'MRR'
        }: ${fEur2(mrr)}</div>
            ${newMrrRow}
            <div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colorRen};margin-right:6px;"></span>Renovaciones: ${fEur2(
          renewals
        )}</div>
            ${newAnnualRow}
            <div style="margin-top:4px;padding-top:4px;border-top:1px solid ${
              theme.palette.divider
            };font-weight:600;">Total: ${fEur2(total)}</div>
            <div style="margin-top:6px;color:${theme.palette.text.secondary};font-weight:400;">
              ${m.activeMonthly.toLocaleString('es-ES')} subs mensuales activas${cancelText}
            </div>
          </div>
        `;
      },
    },
    legend: { position: 'top', horizontalAlign: 'right' },
    grid: { borderColor: theme.palette.divider, strokeDashArray: 4 },
  };

  return (
    <Card>
      <CardHeader title="Forecast de facturación" subheader="MRR + nuevo MRR + renovaciones + nuevas anuales por mes" />
      <Box sx={{ p: 3, pb: 1 }}>
        <ReactApexChart options={options} series={series} type="bar" height={340} />
      </Box>
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.disabled">
          Solo datos Stripe - Se asume MRR constante igual al siguiente mes — la utilidad es prever facturación. Las
          nuevas suscripciones anuales y el nuevo MRR solo se reflejan en el mes en curso; el MRR del mes en curso no
          cuenta las bajas que terminan este mes.
        </Typography>
      </Box>
    </Card>
  );
}

ForecastChart.propTypes = {
  stats: PropTypes.object,
};
